import dayjs from 'dayjs';
import React from 'react';

import {
  adminGlobalSearchApi,
  adminGlobalSearchCountApi,
} from '../../api/globalSearch/globalSearchApi';
import { AdminGlobalSearchCountResponse, AdminGlobalSearchItem } from '../../graphql/API';
import { useAdminRole } from '../../hooks/useAdminRole';

export type GlobalSearchGroupKey = 'users' | 'businesses' | 'offers' | 'reservations' | 'content';

export type GlobalSearchItem = {
  /** Unique across groups, used as React key and for keyboard navigation. */
  key: string;
  id: string;
  group: GlobalSearchGroupKey;
  title: string;
  subtitle?: string;
  meta?: string;
  status?: string;
  picture?: string;
  url: string;
};

export type GlobalSearchGroup = {
  key: GlobalSearchGroupKey;
  label: string;
  items: GlobalSearchItem[];
  /** How many rows match the term in total — `items` only holds the first page. */
  total?: number;
};

export type GlobalSearchTotals = Partial<Record<GlobalSearchGroupKey, number>>;

// Two characters match tens of thousands of rows in production, which is the
// slowest possible query for a result nobody can use — three narrows it a lot.
export const GLOBAL_SEARCH_MIN_CHARS = 3;
const GROUP_LIMIT = 6;
const DEBOUNCE_MS = 400;
/** Repeating a term (backspace, retyping) must not hit the backend again. */
const CACHE_TTL_MS = 2 * 60 * 1000;
const CACHE_MAX_ENTRIES = 150;

const GROUP_LABELS: Record<GlobalSearchGroupKey, string> = {
  users: 'Users',
  businesses: 'Businesses',
  offers: 'Offers',
  reservations: 'Reservations',
  content: 'Content',
};

/** Entity names of the backend query, keyed by the dropdown group they fill. */
const GROUP_ENTITIES: Record<GlobalSearchGroupKey, string> = {
  users: 'users',
  businesses: 'venues',
  offers: 'offers',
  reservations: 'reservations',
  content: 'content',
};

const groupOfEntity = (entity: string): GlobalSearchGroupKey | undefined =>
  (Object.keys(GROUP_ENTITIES) as GlobalSearchGroupKey[]).find(
    (key) => GROUP_ENTITIES[key] === entity,
  );

const CORE_GROUP_KEYS: GlobalSearchGroupKey[] = ['users', 'businesses', 'offers', 'reservations'];

/**
 * What the signed-in admin is allowed to search, mirroring the Cognito groups the
 * backend puts on each entity: the four core entities are readable by superAdmin,
 * admin, city_manager and support, while content is the one a social admin can
 * reach (superAdmin, admin, city_manager, social). The backend enforces the same
 * split, so asking for more would only come back empty.
 */
export type GlobalSearchScope = {
  core: boolean;
  content: boolean;
  enabled: boolean;
  groupKeys: GlobalSearchGroupKey[];
};

export const useGlobalSearchScope = (): GlobalSearchScope => {
  const { isSuperAdmin, isAdmin, isCityManager, isSupport, isSocial } = useAdminRole();

  return React.useMemo(() => {
    const core = isSuperAdmin || isAdmin || isCityManager || isSupport;
    const content = isSuperAdmin || isAdmin || isCityManager || isSocial;
    const groupKeys = [...(core ? CORE_GROUP_KEYS : []), ...(content ? ['content' as const] : [])];

    return { core, content, enabled: groupKeys.length > 0, groupKeys };
  }, [isSuperAdmin, isAdmin, isCityManager, isSupport, isSocial]);
};

const joinMeta = (...parts: Array<string | null | undefined>) =>
  parts.filter(Boolean).join(' · ') || undefined;

const formatDate = (date?: string | null) => (date ? dayjs(date).format('ll') : undefined);

/**
 * One dropdown row out of a backend hit. The backend already projects every entity
 * onto the same shape (title, subtitle, handle, category, market, status, date), so
 * only the wording and the target route differ per group.
 */
const toItem = (
  hit: AdminGlobalSearchItem,
  group: GlobalSearchGroupKey,
  term: string,
): GlobalSearchItem => {
  const id = String(hit.id);
  const base = {
    key: `${group}-${id}`,
    id,
    group,
    picture: hit.picture?.thumbnail || undefined,
    status: hit.status || undefined,
  };

  switch (group) {
    case 'users':
      return {
        ...base,
        title: hit.title || hit.subtitle || `User #${id}`,
        subtitle: hit.subtitle || undefined,
        meta: joinMeta(`ID ${id}`, hit.handle ? `@${hit.handle}` : undefined, hit.marketName),
        url: `/users/${id}/profile`,
      };
    case 'businesses':
      return {
        ...base,
        title: hit.title || hit.subtitle || `Business #${id}`,
        subtitle: hit.subtitle || undefined,
        meta: joinMeta(`ID ${id}`, hit.category, hit.marketName),
        url: `/businesses/${id}/profile`,
      };
    case 'offers':
      return {
        ...base,
        title: hit.title || `Offer #${id}`,
        subtitle: hit.subtitle || undefined,
        meta: joinMeta(`ID ${id}`, hit.category, hit.marketName),
        url: `/offers/${id}/profile`,
      };
    case 'reservations':
      return {
        ...base,
        title: hit.title || `Reservation #${id}`,
        subtitle: hit.subtitle || undefined,
        meta: joinMeta(`ID ${id}`, formatDate(hit.date), hit.marketName),
        url: `/reservations/${id}`,
      };
    case 'content':
    default:
      // Content has no detail route of its own, so every row lands on the Content
      // page pre-filtered by the term that produced it.
      return {
        ...base,
        title: hit.title || `Content #${id}`,
        subtitle: hit.subtitle || undefined,
        meta: joinMeta(`ID ${id}`, formatDate(hit.date)),
        status: hit.category || undefined,
        url: `/content?searchText=${encodeURIComponent(term)}`,
      };
  }
};

type GroupResult = { items: GlobalSearchItem[]; total?: number };
type GroupResults = Partial<Record<GlobalSearchGroupKey, GroupResult>>;

const cacheKeyOf = (group: GlobalSearchGroupKey, search: string, marketID?: string) =>
  `${group}|${marketID ?? ''}|${search.toLowerCase()}`;

/** Splits the flat list of hits into one result per requested group, in backend order. */
const groupHits = (
  hits: AdminGlobalSearchItem[],
  groups: GlobalSearchGroupKey[],
  term: string,
): GroupResults => {
  const results: GroupResults = {};
  groups.forEach((group) => {
    results[group] = { items: [] };
  });

  hits.forEach((hit) => {
    const group = groupOfEntity(hit.entity);
    const result = group ? results[group] : undefined;
    if (!result || result.items.length >= GROUP_LIMIT) return;
    result.items.push(toItem(hit, group as GlobalSearchGroupKey, term));
  });

  // A group that came back short of a full page is already complete: its total is
  // the row count, so it is left out of the counts query.
  groups.forEach((group) => {
    const result = results[group];
    if (result && result.items.length < GROUP_LIMIT) result.total = result.items.length;
  });

  return results;
};

const totalOf = (counts: AdminGlobalSearchCountResponse, group: GlobalSearchGroupKey) => {
  const value = counts[GROUP_ENTITIES[group] as keyof AdminGlobalSearchCountResponse];
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const useGlobalSearch = (
  term: string,
): { groups: GlobalSearchGroup[]; items: GlobalSearchItem[]; loading: boolean } => {
  const { isCityManager, marketId } = useAdminRole();
  const scope = useGlobalSearchScope();
  const marketID = isCityManager && marketId ? String(marketId) : undefined;

  const [results, setResults] = React.useState<GroupResults>({});
  const [loading, setLoading] = React.useState(false);
  // Guards against out-of-order responses: only the latest request may render.
  const requestRef = React.useRef(0);
  const cacheRef = React.useRef(new Map<string, { at: number; result: GroupResult }>());

  const readCache = React.useCallback((key: string) => {
    const entry = cacheRef.current.get(key);

    if (!entry) return undefined;
    if (Date.now() - entry.at > CACHE_TTL_MS) {
      cacheRef.current.delete(key);
      return undefined;
    }

    return entry.result;
  }, []);

  const writeCache = React.useCallback((key: string, result: GroupResult) => {
    const cache = cacheRef.current;

    cache.delete(key);
    cache.set(key, { at: Date.now(), result });

    // Plain LRU: the Map keeps insertion order, so the oldest key comes first.
    while (cache.size > CACHE_MAX_ENTRIES) {
      const oldest = cache.keys().next().value;
      if (oldest === undefined) break;
      cache.delete(oldest);
    }
  }, []);

  React.useEffect(() => {
    const search = term.trim();

    if (search.length < GLOBAL_SEARCH_MIN_CHARS) {
      requestRef.current += 1;
      setResults({});
      setLoading(false);
      return;
    }

    const cached = scope.groupKeys
      .map((key) => ({ key, result: readCache(cacheKeyOf(key, search, marketID)) }))
      .filter((entry): entry is { key: GlobalSearchGroupKey; result: GroupResult } =>
        Boolean(entry.result),
      );

    // Anything already cached (retyping, backspacing) renders with no request at all.
    if (cached.length > 0) {
      setResults((current) =>
        cached.reduce((acc, entry) => ({ ...acc, [entry.key]: entry.result }), current),
      );
    }

    const missing = scope.groupKeys.filter((key) => !cached.some((entry) => entry.key === key));

    if (missing.length === 0) {
      requestRef.current += 1;
      setLoading(false);
      return;
    }

    setLoading(true);

    const timeout = setTimeout(() => {
      const requestId = (requestRef.current += 1);
      const filter = { search, marketID, limit: GROUP_LIMIT };

      // One round trip for the rows of every missing group, then one for the totals
      // of the groups whose page came back full.
      adminGlobalSearchApi({ ...filter, entities: missing.map((key) => GROUP_ENTITIES[key]) })
        .then((hits) => groupHits(hits, missing, search))
        .catch((error) => {
          console.error('Global search failed', error);
          return groupHits([], missing, search);
        })
        .then((grouped) => {
          if (requestId !== requestRef.current) return;

          missing.forEach((key) => {
            const result = grouped[key];
            if (result) writeCache(cacheKeyOf(key, search, marketID), result);
          });
          setResults((current) => ({ ...current, ...grouped }));
          setLoading(false);

          const truncated = missing.filter((key) => grouped[key]?.total === undefined);
          if (truncated.length === 0) return;

          adminGlobalSearchCountApi({
            search,
            marketID,
            entities: truncated.map((key) => GROUP_ENTITIES[key]),
          })
            .then((counts) => {
              const withTotals: GroupResults = {};
              truncated.forEach((key) => {
                const result = grouped[key];
                if (!result) return;
                withTotals[key] = { items: result.items, total: totalOf(counts, key) };
                writeCache(cacheKeyOf(key, search, marketID), withTotals[key] as GroupResult);
              });

              if (requestId !== requestRef.current) return;
              setResults((current) => ({ ...current, ...withTotals }));
            })
            // A failing counter never hides its group: the rows stay, without a total.
            .catch((error) => console.error('Global search count failed', error));
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [term, marketID, scope, readCache, writeCache]);

  const groups = React.useMemo(
    () =>
      scope.groupKeys
        .map((key) => {
          const result = results[key];

          return {
            key,
            label: GROUP_LABELS[key],
            items: result?.items ?? [],
            // A row resolved by id isn't part of the text-search count, so the total
            // can legitimately come back lower than what is on screen.
            total:
              result?.total === undefined ? undefined : Math.max(result.total, result.items.length),
          };
        })
        .filter((group) => group.items.length > 0),
    [results, scope.groupKeys],
  );

  const items = React.useMemo(() => groups.flatMap((group) => group.items), [groups]);

  return { groups, items, loading };
};
